import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";
import { Filter } from "@/shared/lib/filter/filter";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { providesList } from "@/services/backend/_lib/tag-helpers";
import { Lst } from "@/shared/lib/lst/lst";
import { backendApi } from "@/services/backend/api";
import {
  Calculation,
  Parameters,
} from "@/services/backend/vbs/calculations/types/calculation";
import { Selection } from "@/services/backend/vbs/calculations/types/expenses";
import { DocumentState } from "@/services/backend/documents/types/state";

const vbsCalculationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    vbsCalculationCreate: build.mutation<Calculation, CreateRequest>({
      query: (request) => ({
        url: `/vbs/calculation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["VbsCalculation"],
    }),
    vbsCalculationCreateFromOld: build.mutation<
      Calculation,
      CreateFromOldRequest
    >({
      query: (request) => ({
        url: `/vbs/calculation/create-from-old`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: ["VbsCalculation"],
    }),
    vbsCalculationPatch: build.mutation<Calculation, PatchRequest>({
      query: (request) => ({
        url: `/vbs/calculation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCalculation", id: request.id },
        { type: "VbsCalculation", id: "LIST" },
      ],
    }),
    vbsCalculationResetReferenceNumber: build.mutation<
      Calculation,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/vbs/calculation/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "VbsCalculation", id: request.id },
        { type: "VbsCalculation", id: "LIST" },
      ],
    }),
    vbsCalculationShow: build.query<Calculation, ShowRequest>({
      query: (request) => ({
        url: `/vbs/calculation`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "VbsCalculation", id: result.id }] : [],
    }),
    vbsCalculationList: build.query<Lst<Calculation>, Partial<ListRequest>>({
      query: (request) => ({
        url: `/vbs/calculation/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "VbsCalculation"),
    }),
    vbsCalculationUpdateEmployees: build.mutation<
      Calculation,
      UpdateEmployeesRequest
    >({
      query: (request) => ({
        url: `/vbs/calculation/employees`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (result, _error, request) => [
        { type: "VbsCalculation", id: request.id },
        "VbsConfirmation",
        "VbsContract",
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsCalculationUpdateParameters: build.mutation<
      Calculation,
      UpdateParametersRequest
    >({
      query: (request) => ({
        url: `/vbs/calculation/parameters`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCalculation", id: request.id },
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsConfirmation",
        "VbsContract",
        "VbsTreatment",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsCalculationUpdateTravelTime: build.mutation<
      Calculation,
      UpdateTravelTimeRequest
    >({
      query: (request) => ({
        url: `/vbs/calculation/travel-time`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (result, _error, request) => [
        { type: "VbsCalculation", id: request.id },
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsConfirmation",
        "VbsContract",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsCalculationUpdateExpenses: build.mutation<
      Calculation,
      UpdateExpensesRequest
    >({
      query: (request) => ({
        url: `/vbs/calculation/expenses`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (result, _error, request) => [
        { type: "VbsCalculation", id: request.id },
        "VbsPricing",
        "VbsPricingByArea",
        "VbsOffer",
        "VbsConfirmation",
        "VbsContract",
        "VbsWorkOrder",
        "RelationshipsRelationship",
        "RelationshipsRelationshipCommitment",
        "RelationshipsFulfillment",
      ],
    }),
    vbsCalculationUpdateState: build.mutation<Calculation, UpdateStateRequest>({
      query: (request) => ({
        url: `/vbs/calculation/state`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "VbsCalculation", id: request.id },
        "VbsCalculation",
      ],
    }),
    vbsCalculationDelete: build.mutation<undefined, DeleteRequest>({
      query: (request) => ({
        url: `/vbs/calculation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "VbsCalculation", id: request.id },
        { type: "VbsCalculation", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useVbsCalculationPatchMutation,
  useVbsCalculationResetReferenceNumberMutation,
  useVbsCalculationCreateMutation,
  useVbsCalculationCreateFromOldMutation,
  useVbsCalculationShowQuery,
  useVbsCalculationListQuery,
  useVbsCalculationUpdateEmployeesMutation,
  useVbsCalculationUpdateParametersMutation,
  useVbsCalculationUpdateTravelTimeMutation,
  useVbsCalculationUpdateExpensesMutation,
  useVbsCalculationUpdateStateMutation,
  useVbsCalculationDeleteMutation,
} = vbsCalculationApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface CreateFromOldRequest {
  oldCalculationId: string;
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface UpdateStateRequest {
  id: string;
  state: DocumentState;
  stateSetBy: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  ownerType?: Filter<string>;
  owner?: Filter<string>;
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface UpdateEmployeesRequest {
  id: string;
  count: number;
}

export interface UpdateParametersRequest {
  id: string;
  parameters: Parameters;
}

export interface UpdateTravelTimeRequest {
  id: string;
  drives: number;
  hoursPerDrive: number;
}

export interface UpdateExpensesRequest {
  id: string;
  selection: Selection;
  perDiemDays: number;
  overnightNights: number;
}

export interface DeleteRequest {
  id: string;
}

export const updateVbsCalculationCache = updateCache(
  vbsCalculationApi,
  "vbsCalculationShow",
);
